//dependencies
import { format } from "date-fns";
import React from "react";

//components
import { Avatar, ICellRendererParams, Link, PYColumnDef, Text } from "@performyard/py-components";

import AvatarGroup from "../../../components/AvatarGroup";

//utils
import toEmployeeUrl from "../../../utils/URLs/Employee";
import { Feedback } from "../../UserFeedback/utils";

const SharedWithTextMap: Record<string, string> = {
	subject: "Subject",
	manager: "Manager",
	all: "Everyone",
	everyone: "Everyone",
	me: "Me",
	"subject-+-manager": "Subject & Subject's Manager",
	"subject-manager": "Subject's Manager",
};

const SubjectCellRenderer = (params: ICellRendererParams<Feedback>) => {
	const feedback = params.data;
	if (!feedback) return null;

	const subjects = feedback.subject || [];

	if (subjects.length < 2) {
		return (
			<div className="flex h-full items-center">
				{subjects.map((subject) => (
					<Link key={subject._id} href={toEmployeeUrl(subject._id)} isExternal>
						<div className="flex items-center gap-1">
							<Avatar size={20} id={subject._id} alt={`${subject.name}'s avatar`} />
							<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
								{subject.name}
							</Text>
						</div>
					</Link>
				))}
			</div>
		);
	}

	return (
		<div className="flex h-full items-center">
			<AvatarGroup canShowModal employees={subjects} maxLength={5} />
		</div>
	);
};

const AuthorCellRenderer = (params: ICellRendererParams<Feedback>) => {
	const feedback = params.data;
	if (!feedback) return null;

	const authors = feedback.author || [];

	if (authors.length < 2) {
		return (
			<div className="flex h-full items-center">
				{authors.map((author) => (
					<Link key={author._id} href={toEmployeeUrl(author._id)} isExternal>
						<div className="flex items-center gap-1">
							<Avatar size={20} id={author._id} alt={`${author.name}'s avatar`} />
							<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
								{author.name}
							</Text>
						</div>
					</Link>
				))}
			</div>
		);
	}

	return (
		<div className="flex h-full items-center">
			<AvatarGroup canShowModal employees={authors} maxLength={5} />
		</div>
	);
};

const SharedWithCellRenderer = (params: ICellRendererParams<Feedback>) => {
	const feedback = params.data;
	if (!feedback) return null;

	return (
		<div className="flex h-full items-center">
			<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
				{SharedWithTextMap[feedback.shared_with] || ""}
			</Text>
		</div>
	);
};

const DateCellRenderer = (params: ICellRendererParams<Feedback>) => {
	const feedback = params.data;
	if (!feedback) return null;

	const lastUpdated = new Date(feedback.last_updated);

	return (
		<div className="flex h-full items-center">
			<Text size="paragraph-3">{format(lastUpdated, "MMM d, yyyy")}</Text>
		</div>
	);
};

const ContentCellRenderer = (params: ICellRendererParams<Feedback>) => {
	const feedback = params.data;
	if (!feedback) return null;

	return (
		<div className="flex h-full items-center">
			<Link href={`/view-feedback/${feedback._id}`} isExternal>
				<Text size="paragraph-3" dangerouslySetClassName="py-line-clamp-1">
					{feedback.content || ""}
				</Text>
			</Link>
		</div>
	);
};

const HashtagsCellRenderer = (params: ICellRendererParams<Feedback>) => {
	const feedback = params.data;
	if (!feedback) return null;

	return (
		<div className="flex h-full items-center">
			<Text size="caption" dangerouslySetClassName="py-line-clamp-1">
				{feedback.categories?.join(" ") || ""}
			</Text>
		</div>
	);
};

const RequestedCellRenderer = (params: ICellRendererParams<Feedback>) => {
	const feedback = params.data;
	if (!feedback) return null;

	return (
		<div className="flex h-full items-center">
			<Text size="paragraph-3">{feedback.response_to_request ? "Yes" : "No"}</Text>
		</div>
	);
};

export const getCompanyFeedbackColumns = (): PYColumnDef<Feedback>[] => {
	return [
		{
			field: "subject.name",
			headerName: "Subject",
			sortable: true,
			resizable: false,
			cellRenderer: SubjectCellRenderer,
		},
		{
			field: "author.name",
			headerName: "Author",
			sortable: true,
			resizable: false,
			cellRenderer: AuthorCellRenderer,
		},
		{
			field: "shared_with",
			headerName: "Shared With",
			sortable: true,
			resizable: false,
			width: 98,
			cellRenderer: SharedWithCellRenderer,
		},
		{
			field: "last_updated",
			headerName: "Date",
			sortable: true,
			resizable: false,
			width: 100,
			cellRenderer: DateCellRenderer,
		},
		{
			field: "content",
			headerName: "Content",
			sortable: false,
			resizable: false,
			flex: 1,
			cellRenderer: ContentCellRenderer,
		},
		{
			field: "categories",
			headerName: "Hashtags",
			sortable: true,
			resizable: false,
			cellRenderer: HashtagsCellRenderer,
		},
		{
			field: "response_to_request",
			headerName: "Requested",
			sortable: true,
			resizable: false,
			width: 96,
			cellRenderer: RequestedCellRenderer,
		},
	];
};
